import { parseOrThrow } from '@shared/utils/validation-error';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  limit,
  Timestamp,
} from 'firebase/firestore';
import { z } from 'zod';
import { db } from '@/firebase/config';
import type { Invoice, InvoiceStatus, ID } from '@shared/types';
import {
  buildBaseFields,
  buildUpdateFields,
  fromSnapshot,
  generateCode,
  nextDocumentBranchNumber,
  writeAuditLog,
} from '@shared/utils';
import { fetchDelivery } from '@modules/delivery';
import { updateOrderStatus, updateOrderAggregates, completeOrder, reopenOrder } from '@modules/order';

const lineItemSchema = z.object({
  id: z.string(),
  // 空白行（帳票レイアウト用）の保持を許容するため空文字を許可する（見積側スキーマと整合）
  title: z.string(),
  qty: z.number().min(0),
  unitId: z.string(),
  // 値引き行に対応するため負の単価・金額を許容する（Infinity/NaN は拒否）
  unitPrice: z.number().finite(),
  amount: z.number().finite(),
  taxRate: z.union([z.literal(10), z.literal(8), z.literal('exempt')]),
});

const invoiceCreateSchema = z.object({
  customerId: z.string().min(1, '顧客は必須です'),
  orderId: z.string().nullable().default(null),
  assignedUserId: z.string().nullable().default(null),
  issueDate: z.instanceof(Timestamp),
  dueDate: z.instanceof(Timestamp),
  closingDate: z.instanceof(Timestamp).nullable().default(null),
  subtotal: z.number().min(0, '小計は0以上です'),
  taxAmount: z.number().min(0, '消費税は0以上です'),
  total: z.number().min(0, '合計は0以上です'),
  notes: z.string().nullable().default(null),
  paymentTiming: z.enum(['prepaid', 'postpaid']).default('postpaid'),
  prepaidDueDate: z.instanceof(Timestamp).nullable().default(null),
  // C2 追加フィールド
  lineItems: z.array(lineItemSchema).default([]),
  customerNumber: z.string().nullable().default(null),
  tradeTypeId: z.string().nullable().default(null),
  bankId: z.string().nullable().default(null),
  deliveryId: z.string().nullable().default(null),
  taxCalcMode: z.enum(['floor', 'ceil', 'round', 'manual']).default('floor'),
  advance: z.number().min(0).finite().default(0),
  totalgst: z.number().min(0).finite().default(0),
  grandtotal: z.number().min(0).finite().default(0),
  // AI秘書の書込ツールだけが true を渡す（本体UIのフォームはこのフィールドを送らない）。
  // 人がレビュー確定すると deleteField() で消える。docs/AI_SECRETARY_TOOL_ARCHITECTURE.md §3.5
  aiPendingReview: z.boolean().optional(),
});

const invoiceUpdateSchema = invoiceCreateSchema.omit({ customerId: true }).partial();

export type InvoiceCreateInput = z.infer<typeof invoiceCreateSchema>;
export type InvoiceUpdateInput = z.infer<typeof invoiceUpdateSchema>;

const COL = 'invoices';

/**
 * 案件に紐づく未削除の請求書合計を再集計し、受注一覧の請求額（order.invoiceTotal）へ反映する。
 * 請求書の作成・更新・削除時に呼び出し、削除後は残った請求書の合計（0円含む）へ連動させる。
 */
async function syncOrderInvoiceTotal(orderId: ID | null | undefined, userId: ID): Promise<void> {
  if (!orderId) return;
  const snap = await getDocs(
    query(collection(db, COL), where('orderId', '==', orderId), where('isDeleted', '==', false))
  );
  const invoiceTotal = snap.docs.reduce(
    (s, d) => s + ((d.data()['total'] as number | undefined) ?? 0),
    0
  );
  await updateOrderAggregates(orderId, { invoiceTotal }, userId).catch(() => undefined);
}

export async function fetchInvoices(filters?: {
  customerId?: ID;
  status?: InvoiceStatus;
  isLocked?: boolean;
}): Promise<Invoice[]> {
  let q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    orderBy('issueDate', 'desc'),
    limit(500)
  );
  if (filters?.customerId) {
    q = query(q, where('customerId', '==', filters.customerId));
  }
  if (filters?.status) {
    q = query(q, where('status', '==', filters.status));
  }
  if (filters?.isLocked !== undefined) {
    q = query(q, where('isLocked', '==', filters.isLocked));
  }
  const snap = await getDocs(q);
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }) as Invoice);
}

export async function fetchInvoice(id: ID): Promise<Invoice | null> {
  const snap = await getDoc(doc(db, COL, id));
  return fromSnapshot<Invoice>(snap);
}

/**
 * 重複判定用に明細行を正規化する。
 * id・amount（派生値）は比較対象から除外し、品名・数量・単位・単価・税率のみで比較する。
 */
function normalizeLineItemsForDuplicateCheck(
  lineItems: InvoiceCreateInput['lineItems'],
): Array<{ title: string; qty: number; unitId: string; unitPrice: number; taxRate: 10 | 8 | 'exempt' }> {
  return lineItems.map((it) => ({
    title: it.title,
    qty: it.qty,
    unitId: it.unitId,
    unitPrice: it.unitPrice,
    taxRate: it.taxRate,
  }));
}

/**
 * 同一案件・同一顧客で、明細と合計金額が完全に一致する未削除の請求書が既に存在するか検証する。
 * 一致する場合は重複として保存を拒否する（発行日などの日時は比較対象外）。
 * 納品書（createDelivery）の重複チェックと同一仕様。
 */
async function assertNoDuplicateInvoice(input: InvoiceCreateInput): Promise<void> {
  if (!input.orderId) return;
  const effectiveGrandtotal = input.grandtotal > 0 ? input.grandtotal : input.total;
  const snap = await getDocs(query(collection(db, COL), where('orderId', '==', input.orderId)));
  const targetItems = JSON.stringify(normalizeLineItemsForDuplicateCheck(input.lineItems));
  const isDuplicate = snap.docs.some((d) => {
    const data = d.data() as Record<string, unknown>;
    // 論理削除済みは重複対象外
    if (data['isDeleted'] === true) return false;
    if ((data['customerId'] as string | undefined) !== input.customerId) return false;
    if (((data['grandtotal'] as number | undefined) ?? 0) !== effectiveGrandtotal) return false;
    const items = (data['lineItems'] as InvoiceCreateInput['lineItems'] | undefined) ?? [];
    return JSON.stringify(normalizeLineItemsForDuplicateCheck(items)) === targetItems;
  });
  if (isDuplicate) {
    throw new Error('同一内容の請求書が既に存在するため、保存できません。');
  }
}

export async function createInvoice(
  data: InvoiceCreateInput,
  userId: ID
): Promise<Invoice> {
  // parseOrThrow は zod の Input 型（デフォルト適用前）と Output 型の両立を要求するため、
  // デフォルト適用済みの Output 型として明示する（実行時の値は zod が既に検証・補完済み）。
  const validated = parseOrThrow(invoiceCreateSchema, data) as InvoiceCreateInput;
  await assertNoDuplicateInvoice(validated);
  // 案件帳票は「受注番号-枝番」(0001-01) を code に持つ。案件に紐づかない場合のみ PREFIX-00001。
  const branch = await nextDocumentBranchNumber('invoices', validated.orderId ?? null);
  const code = branch ?? (await generateCode('IN'));
  const effectiveGrandtotal = validated.grandtotal > 0 ? validated.grandtotal : validated.total;
  const payload = {
    ...validated,
    code,
    status: 'draft' as InvoiceStatus,
    paidAmount: 0,
    remainingAmount: effectiveGrandtotal,
    grandtotal: effectiveGrandtotal,
    isLocked: false,
    ...buildBaseFields(userId),
  };
  const ref = await addDoc(collection(db, COL), payload);
  await writeAuditLog({
    entityType: 'invoice',
    entityId: ref.id,
    action: 'create',
    userId,
    after: validated,
  });
  await syncOrderInvoiceTotal(payload.orderId, userId);
  return { id: ref.id, ...payload } as unknown as Invoice;
}

export async function createInvoiceFromOrder(
  orderId: ID,
  userId: ID
): Promise<Invoice> {
  const orderSnap = await getDoc(doc(db, 'orders', orderId));
  if (!orderSnap.exists()) throw new Error('受注が見つかりません。');
  const order = orderSnap.data();
  const now = Timestamp.now();
  const dueDate = Timestamp.fromMillis(now.toMillis() + 30 * 24 * 60 * 60 * 1000);
  const data: InvoiceCreateInput = {
    customerId: order['customerId'] as string,
    orderId,
    assignedUserId: (order['assignedUserId'] as string | null | undefined) ?? null,
    issueDate: now,
    dueDate,
    closingDate: null,
    subtotal: order['subtotal'] as number,
    taxAmount: order['taxAmount'] as number,
    total: order['total'] as number,
    notes: (order['notes'] as string | null) ?? null,
    paymentTiming: 'postpaid',
    prepaidDueDate: null,
    lineItems: [],
    customerNumber: null,
    tradeTypeId: null,
    bankId: null,
    deliveryId: null,
    taxCalcMode: 'floor',
    advance: 0,
    totalgst: 0,
    grandtotal: 0,
  };
  const invoice = await createInvoice(data, userId);
  await updateOrderStatus(orderId, 'in_progress', userId).catch((err: unknown) => {
    const msg = err instanceof Error ? err.message : String(err);
    if (!msg.includes('見つかりません') && !msg.includes('not-found')) {
      throw new Error(`受注ステータスの更新に失敗しました: ${msg}`);
    }
  });
  return invoice;
}

export async function createInvoiceFromDelivery(
  deliveryId: ID,
  userId: ID,
  /** 継承値への上書き（例: AI経路の issueDate/dueDate/aiPendingReview）。1回の作成で原子的に反映する。 */
  overrides?: Partial<InvoiceCreateInput>
): Promise<Invoice> {
  const delivery = await fetchDelivery(deliveryId);
  if (!delivery) throw new Error('納品書が見つかりません。');

  const data: InvoiceCreateInput = {
    customerId: delivery.customerId,
    orderId: delivery.orderId ?? null,
    assignedUserId: delivery.assignedUserId ?? null,
    deliveryId,
    issueDate: Timestamp.now(),
    dueDate: Timestamp.fromMillis(Timestamp.now().toMillis() + 30 * 24 * 60 * 60 * 1000),
    closingDate: null,
    lineItems: delivery.lineItems ?? [],
    subtotal: delivery.subtotal,
    taxAmount: delivery.totalgst,
    total: delivery.grandtotal,
    advance: delivery.advance ?? 0,
    totalgst: delivery.totalgst,
    grandtotal: delivery.grandtotal,
    taxCalcMode: delivery.taxCalcMode ?? 'floor',
    notes: delivery.printNote,
    paymentTiming: 'postpaid',
    prepaidDueDate: null,
    customerNumber: null,
    tradeTypeId: null,
    bankId: null,
  };

  const invoice = await createInvoice({ ...data, ...overrides }, userId);

  if (delivery.orderId) {
    await updateOrderStatus(delivery.orderId, 'in_progress', userId).catch((err: unknown) => {
      const msg = err instanceof Error ? err.message : String(err);
      if (!msg.includes('見つかりません') && !msg.includes('not-found')) {
        throw new Error(`受注ステータスの更新に失敗しました: ${msg}`);
      }
    });
  }

  return invoice;
}

export async function updateInvoice(
  id: ID,
  data: InvoiceUpdateInput,
  userId: ID
): Promise<Invoice> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('請求書が見つかりません。');
  const before = snap.data();
  if (before['isLocked']) throw new Error('締め済みの請求書は変更できません。');
  const validated = parseOrThrow(invoiceUpdateSchema, data);
  const patch = { ...validated, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'invoice',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: data,
  });
  // 金額・案件の変更を受注集計へ反映（案件が付け替えられた場合は旧案件も再集計）
  const prevOrderId = (before['orderId'] as ID | null | undefined) ?? null;
  const nextOrderId = (validated.orderId ?? prevOrderId) as ID | null;
  await syncOrderInvoiceTotal(nextOrderId, userId);
  if (prevOrderId && prevOrderId !== nextOrderId) {
    await syncOrderInvoiceTotal(prevOrderId, userId);
  }
  return { id, ...before, ...patch } as unknown as Invoice;
}

/**
 * 案件の請求をすべて出し終わったら、案件を「完了」にする。
 *
 * 1案件に請求書が複数ぶら下がる（分割請求・月次請求）ため、**1枚目の発行で完了にしてはいけない**。
 * 「未発行(draft)の請求書が1枚も残っていない」ことを確認してから完了にする。
 * 取消(cancelled)は「これから出す請求」ではないので、未発行には数えない。
 *
 * 請求書を数えるのは invoice 側の責務。order 側から invoice を参照すると循環するため、
 * order 側は「完了にする」という単純な操作（completeOrder）だけを公開している。
 * 失注・完了済みを動かさないガードは completeOrder が持つ。
 */
async function completeOrderIfAllInvoicesIssued(
  orderId: ID | null | undefined,
  userId: ID
): Promise<void> {
  if (!orderId) return; // 案件に紐づかない単独の請求書
  // isDeleted + orderId の複合インデックスは firestore.indexes.json に定義済み
  const q = query(
    collection(db, COL),
    where('isDeleted', '==', false),
    where('orderId', '==', orderId)
  );
  const snap = await getDocs(q);
  const invoices = snap.docs.map((d) => d.data() as Invoice);
  if (invoices.length === 0) return;
  if (invoices.some((inv) => inv.status === 'draft')) return; // まだ出していない請求が残っている
  await completeOrder(orderId, userId);
}

export async function issueInvoice(id: ID, userId: ID): Promise<Invoice> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('請求書が見つかりません。');
  const before = snap.data();
  if (before['isLocked']) throw new Error('締め済みの請求書は変更できません。');
  const patch = { status: 'issued' as InvoiceStatus, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'invoice',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: { status: 'issued' },
  });

  // 手動でのステータス更新忘れを防ぐため、案件の請求が出揃ったら自動で完了にする。
  // 案件の更新に失敗しても請求書の発行は成功させる（帳票を人質にしない。
  // 既存の syncOrderInvoiceTotal も同じ方針）。
  await completeOrderIfAllInvoicesIssued(before['orderId'] as ID | null, userId).catch(
    () => undefined
  );

  return { id, ...before, ...patch } as unknown as Invoice;
}

/**
 * 発行済の請求書を「未発行(draft)」へ戻す。印刷/PDF保存で誤って発行済にした場合の取り消し用。
 * 締め済み(isLocked)、および入金があるもの（status が paid / partially_paid、または paidAmount>0）は戻せない。
 */
export async function unissueInvoice(id: ID, userId: ID): Promise<Invoice> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('請求書が見つかりません。');
  const before = snap.data();
  if (before['isLocked']) throw new Error('締め済みの請求書は変更できません。');
  const status = before['status'] as InvoiceStatus;
  if (status === 'draft') throw new Error('この請求書は既に未発行です。');
  const paidAmount = (before['paidAmount'] as number | undefined) ?? 0;
  if (paidAmount > 0 || status === 'paid' || status === 'partially_paid') {
    throw new Error('入金済みの請求書は未発行に戻せません。');
  }
  const patch = { status: 'draft' as InvoiceStatus, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'invoice',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: { status: 'draft' },
  });

  // 発行を取り消した＝「請求が出揃った」という完了の前提が崩れた。
  // 完了にしたのは自動化なので、自動化が戻す（そうしないと「未発行の請求書がある
  // 完了案件」という矛盾が残る）。失注は人の判断なので戻さない（ガードは reopenOrder）。
  await reopenOrder(before['orderId'] as ID | null, userId).catch(() => undefined);

  return { id, ...before, ...patch } as unknown as Invoice;
}

export async function deleteInvoice(id: ID, userId: ID): Promise<void> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('請求書が見つかりません。');
  if (snap.data()['isLocked']) throw new Error('締め済みの請求書は削除できません。');
  const orderId = (snap.data()['orderId'] as ID | null | undefined) ?? null;
  await updateDoc(ref, { isDeleted: true, ...buildUpdateFields(userId) });
  await writeAuditLog({
    entityType: 'invoice',
    entityId: id,
    action: 'delete',
    userId,
    before: snap.data(),
  });
  // 削除後、残った請求書の合計（無ければ0円）へ受注集計を連動させる
  await syncOrderInvoiceTotal(orderId, userId);
}

export async function cancelInvoice(id: ID, userId: ID): Promise<Invoice> {
  const ref = doc(db, COL, id);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('請求書が見つかりません。');
  const before = snap.data();
  if (before['isLocked']) throw new Error('締め済みの請求書は変更できません。');
  const patch = { status: 'cancelled' as InvoiceStatus, ...buildUpdateFields(userId) };
  await updateDoc(ref, patch);
  await writeAuditLog({
    entityType: 'invoice',
    entityId: id,
    action: 'update',
    userId,
    before,
    after: { status: 'cancelled' },
  });
  return { id, ...before, ...patch } as unknown as Invoice;
}
